import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, BellOff, CalendarPlus, CheckCircle2, ExternalLink, Mail, MapPin, Search, XCircle } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip, type ChipTone } from '../interviews/Chip'
import {
  useCancelServiceBooking, useCaptureServiceBooking, useCountRepairAsService, useDismissServiceCandidate,
  useGetServiceAddressMatches, useGetServiceAddressPlace, useGetServiceBoard, useGetServiceDayDriver,
} from '../../hooks/backend/fleet'
import { writeErrorMessage } from '../../utils/writeError'
import { DUE_STATE_LABEL, dueTone, km, kmEntry, shortDate, type DueState, type ServiceBoardRow } from '../../utils/serviceBoard'
import { UploadStampDialog } from './ServiceStampDialogs'

/**
 * Service bookings, and the services found in the repair lines.
 *
 * A BOOKING CAPTURED HERE SWITCHES THE DUE ALERT OFF FOR THAT CAR, UNTIL ITS
 * DAY. It is recorded as confirmed, which is what the 05:10 run and the daily
 * email skip on, but only up to the booked day (migration 3302). If the day
 * passes and no stamp has been uploaded, the car is back in the alerts and in
 * the daily email.
 *
 * ONLY A STAMP CLOSES A BOOKING. Record service and a counted repair line
 * record the service but leave the booking open, in red, until the stamped page
 * is uploaded or the booking is cancelled. The stamp is checked against the
 * booking first (vehicle_service_record_stamp_v1).
 *
 * The address is required and comes from Google Maps, and capturing emails the
 * car's day shift driver from the Uber trips (migration 3303). The owner is not
 * told. The email is not recorded as the formal written instruction that a
 * missed-service conduct case rests on; a manager still records that.
 */

export type BookingRow = {
  booking_id: string
  ref: string
  vehicle_id: string
  plate: string | null
  vehicle_name: string | null
  provider_name: string
  address: string | null
  provider_phone: string | null
  scheduled_at: string
  status: string
  is_open: boolean
  alerts_paused: boolean
  date_passed: boolean
  days_past: number | null
  odometer_at_booking: number | null
  booked_by: string | null
  booked_at: string | null
  notes: string | null
  cancelled_at: string | null
  cancelled_by: string | null
  cancel_reason: string | null
  completed_at: string | null
  service_event_id: string | null
  serviced_on: string | null
  serviced_km: number | null
  stamp_photos: number
  due_state: DueState | null
  km_to_service: number | null
  maps_url: string | null
  driver_name: string | null
  driver_informed_at: string | null
  driver_informed_note: string | null
}

export type RepairCandidate = {
  maintenance_line_id: string
  vehicle_id: string
  plate: string | null
  category: string
  found_on: string
  date_exact: boolean
  odometer_km: number | null
  description: string | null
  source: string | null
  last_serviced_on: string | null
  baseline_km: number | null
  baseline_provisional: boolean | null
}

const link =
  'rounded text-xs font-medium text-[hsl(var(--brand))] underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring'

function today(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Johannesburg' })
}

export function dateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-ZA', {
    timeZone: 'Africa/Johannesburg',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const STATUS_LABEL: Record<string, string> = {
  proposed: 'Proposed',
  confirmed: 'Booked',
  attended: 'At the workshop',
  completed: 'Serviced',
  cancelled: 'Cancelled',
  no_show: 'Did not go in',
}

function statusTone(b: BookingRow): ChipTone {
  if (b.is_open && b.date_passed) return 'red'
  if (b.is_open) return 'brand'
  if (b.status === 'completed') return 'green'
  return 'neutral'
}

type PlaceMatch = { place_id: string; name: string; detail: string; text: string }
type Place = {
  place_id: string
  name: string
  address: string
  latitude: number | null
  longitude: number | null
  maps_url: string
  phone: string | null
}
type DayDriver = {
  driver_id: string
  driver_name: string
  has_email: boolean
  day_trips: number
  last_day: string
  window_days: number
} | null

/**
 * The workshop's address, from Google Maps. Typing searches Google Places
 * through the Worker (getServiceAddressMatches); picking a match fetches the
 * formatted address, coordinates and Maps link (getServiceAddressPlace). A
 * typed address that was never picked is not accepted: the driver is emailed a
 * map link, and it has to point somewhere.
 */
function AddressSearch({
  place,
  onPick,
  onClear,
}: {
  place: Place | null
  onPick: (p: Place) => void
  onClear: () => void
}) {
  const [text, setText] = useState('')
  // One Google session per search, so the typing and the pick bill as one.
  const [session] = useState(() => crypto.randomUUID())
  const search = useGetServiceAddressMatches<PlaceMatch[]>()
  const details = useGetServiceAddressPlace<Place>()
  const runSearch = search.trigger

  useEffect(() => {
    const q = text.trim()
    if (place || q.length < 3) return
    const t = setTimeout(() => {
      runSearch({ input: q, session }).result.catch(() => {
        // The error is on search.error and shown below.
      })
    }, 450)
    return () => clearTimeout(t)
  }, [text, place, session, runSearch])

  async function pick(m: PlaceMatch) {
    try {
      const p = await details.trigger({ place_id: m.place_id, session }).result
      if (p) onPick({ ...p, name: p.name || m.name, address: p.address || m.text })
    } catch (err) {
      toast.error('Google Maps could not give that address', { description: writeErrorMessage(err) })
    }
  }

  if (place) {
    return (
      <div className="rounded-md border border-border bg-muted/40 p-2 text-sm">
        <div className="flex items-start gap-2">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--brand))]" />
          <div className="min-w-0 flex-1">
            <div className="font-medium text-foreground">{place.name}</div>
            <div className="text-xs text-muted-foreground">{place.address}</div>
            <a href={place.maps_url} target="_blank" rel="noopener noreferrer" className={`${link} mt-1 inline-flex items-center gap-1`}>
              Open in Google Maps <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <button type="button" className={link} onClick={() => { setText(''); onClear() }}>
            Change
          </button>
        </div>
      </div>
    )
  }

  const results = text.trim().length >= 3 ? search.data ?? [] : []
  return (
    <div className="space-y-1">
      <Input
        id="bk-address"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Search Google Maps: workshop name or street"
        autoComplete="off"
      />
      {(search.loading || details.loading) && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Spinner className="h-3 w-3" /> {details.loading ? 'Getting the address' : 'Searching Google Maps'}
        </p>
      )}
      {search.error && <p className="text-xs text-red-700 dark:text-red-400">Google Maps search failed: {search.error}</p>}
      {results.length > 0 && (
        <ul className="max-h-48 overflow-y-auto rounded-md border border-border bg-card">
          {results.map((m) => (
            <li key={m.place_id}>
              <button
                type="button"
                className="flex w-full items-start gap-2 px-2 py-1.5 text-left text-sm hover:bg-muted/60"
                onClick={() => pick(m)}
                disabled={details.loading}
              >
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span>
                  <span className="font-medium text-foreground">{m.name}</span>
                  {m.detail && <span className="block text-xs text-muted-foreground">{m.detail}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!search.loading && text.trim().length >= 3 && search.data && results.length === 0 && (
        <p className="text-xs text-muted-foreground">Google Maps found nothing for that. Try the workshop name and the suburb.</p>
      )}
    </div>
  )
}

export function CaptureBookingDialog({
  vehicle,
  onSaved,
  trigger,
}: {
  // Given from a row on the Service due tab; left out on the bookings tab,
  // where the person picks the car.
  vehicle?: { vehicle_id: string; plate: string | null } | null
  onSaved: () => void
  trigger: 'link' | 'button'
}) {
  const [open, setOpen] = useState(false)
  const [vehicleId, setVehicleId] = useState('')
  const [provider, setProvider] = useState('')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('08:00')
  const [place, setPlace] = useState<Place | null>(null)
  const [phone, setPhone] = useState('')
  const [notes, setNotes] = useState('')
  const save = useCaptureServiceBooking<{ ref?: string; driver_note?: string }>()
  const cars = useGetServiceBoard<ServiceBoardRow[]>()
  const dayDriver = useGetServiceDayDriver<DayDriver>()
  const loadCars = cars.trigger
  const loadDriver = dayDriver.trigger

  useEffect(() => {
    if (!open) return
    setVehicleId(vehicle?.vehicle_id ?? '')
    setProvider('')
    setDate('')
    setTime('08:00')
    setPlace(null)
    setPhone('')
    setNotes('')
    if (!vehicle) loadCars({ scope: 'all' })
  }, [open, vehicle, loadCars])

  useEffect(() => {
    if (open && vehicleId) loadDriver({ vehicle_id: vehicleId })
  }, [open, vehicleId, loadDriver])

  // Cars that already have an open booking are left out: the database refuses
  // a second one, and the list should not offer what will be refused.
  const choices = useMemo(
    () =>
      (cars.data ?? [])
        .filter((r) => !r.open_booking_id)
        .slice()
        .sort((a, b) => (a.plate ?? '').localeCompare(b.plate ?? '')),
    [cars.data],
  )
  const plate = vehicle?.plate ?? choices.find((c) => c.vehicle_id === vehicleId)?.plate ?? null

  function pickPlace(p: Place) {
    setPlace(p)
    // The place's own name and phone fill the fields only when they are empty:
    // what Musa typed wins.
    if (provider.trim() === '') setProvider(p.name)
    if (phone.trim() === '' && p.phone) setPhone(p.phone)
  }

  async function submit() {
    if (!place) return
    try {
      // South Africa keeps one offset all year, so the time typed is SAST.
      const r = await save.trigger({
        vehicle_id: vehicleId,
        provider_name: provider.trim(),
        scheduled_at: `${date}T${time || '08:00'}:00+02:00`,
        address: place.address,
        place_id: place.place_id,
        latitude: place.latitude,
        longitude: place.longitude,
        maps_url: place.maps_url,
        provider_phone: phone.trim() || null,
        notes: notes.trim() || null,
      }).result
      toast.success(`Booking ${r?.ref ?? ''} captured for ${plate ?? 'the vehicle'}`, {
        description: [
          'The due alert is off until the booked day.',
          r?.driver_note ?? null,
        ].filter(Boolean).join(' '),
      })
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not capture that booking', { description: writeErrorMessage(err) })
    }
  }

  const drv = vehicleId ? dayDriver.data : null
  const ready = vehicleId !== '' && provider.trim() !== '' && date !== '' && place !== null
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger === 'link' ? (
          <button type="button" className={`${link} mt-1 block`} aria-label={`Capture a service booking for ${vehicle?.plate ?? 'a vehicle'}`}>
            Capture booking
          </button>
        ) : (
          <Button type="button" size="sm">
            <CalendarPlus className="mr-2 h-4 w-4" /> Capture booking
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Service booking{vehicle?.plate ? `, ${vehicle.plate}` : ''}</DialogTitle>
          <DialogDescription>
            Record a booking you have made with the workshop. The car&apos;s day shift driver is emailed
            the booking with a map, and the due alert switches off until the booked day. Upload the stamp
            after the service to close it. The owner is not told.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {!vehicle && (
            <div className="space-y-1.5">
              <Label htmlFor="bk-vehicle">Vehicle</Label>
              <NativeSelect id="bk-vehicle" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                <option value="">{cars.loading ? 'Loading the fleet…' : 'Choose the car'}</option>
                {choices.map((c) => (
                  <option key={c.vehicle_id} value={c.vehicle_id}>
                    {[c.plate ?? '—', c.vehicle_name].filter(Boolean).join(', ')} ({DUE_STATE_LABEL[c.due_state]})
                  </option>
                ))}
              </NativeSelect>
              {cars.error && <p className="text-xs text-red-700 dark:text-red-400">The fleet could not be read: {cars.error}</p>}
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="bk-address">Address</Label>
            <AddressSearch place={place} onPick={pickPlace} onClear={() => setPlace(null)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bk-provider">Workshop or dealer</Label>
            <Input id="bk-provider" value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="Filled from the map, or type it" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="bk-date">Booked for</Label>
              <Input id="bk-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bk-time">Time</Label>
              <Input id="bk-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          </div>
          {date !== '' && date < today() && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              That date has passed, so the driver will not be emailed. If the car has already been serviced,
              upload the stamp instead.
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="bk-phone">
              Workshop phone <span className="ml-1 font-normal text-muted-foreground">optional</span>
            </Label>
            <Input id="bk-phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bk-notes">
              Note for the driver <span className="ml-1 font-normal text-muted-foreground">optional, goes in the email</span>
            </Label>
            <Textarea id="bk-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Ask for Thabo at the service desk" />
          </div>
          {vehicleId && (
            <div className="flex items-start gap-2 rounded-md border border-border p-2 text-xs">
              <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              {dayDriver.loading ? (
                <span className="text-muted-foreground">Finding the day shift driver in the Uber trips…</span>
              ) : dayDriver.error ? (
                <span className="text-red-700 dark:text-red-400">The day shift driver could not be read: {dayDriver.error}</span>
              ) : drv ? (
                <span>
                  <span className="font-medium text-foreground">{drv.driver_name}</span> is emailed the booking:
                  the day shift driver on the Uber trips, {drv.day_trips} day shift trip{drv.day_trips === 1 ? '' : 's'} in
                  this car in the last {drv.window_days} days, last on {shortDate(drv.last_day)}.
                  {!drv.has_email && (
                    <span className="block text-amber-700 dark:text-amber-400">
                      There is no email address on their record, so tell them yourself.
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-amber-700 dark:text-amber-400">
                  No active driver drove this car on a day shift in the Uber trips of the last 14 days, so
                  nobody will be emailed. Tell the driver yourself.
                </span>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" disabled={save.loading || !ready} onClick={submit}>
            {save.loading ? <Spinner className="mr-2 h-4 w-4" /> : <BellOff className="mr-2 h-4 w-4" />}
            Capture booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CancelBookingDialog({ booking, onSaved }: { booking: BookingRow; onSaved: () => void }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const cancel = useCancelServiceBooking()

  useEffect(() => {
    if (open) setReason('')
  }, [open])

  async function submit() {
    try {
      await cancel.trigger({ booking_id: booking.booking_id, reason: reason.trim() }).result
      toast.success(`Booking ${booking.ref} cancelled`, {
        description: 'The due alert on this car is back on from the next morning run.',
      })
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not cancel that booking', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" className={`${link} ml-3`}>
          Cancel
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel {booking.ref}, {booking.plate ?? 'vehicle'}</DialogTitle>
          <DialogDescription>
            The booking stays on the record with your reason. The due alert on this car switches back
            on. If the booking has only moved, cancel it and capture the new date.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor={`bk-cancel-${booking.booking_id}`}>Reason</Label>
          <Textarea id={`bk-cancel-${booking.booking_id}`} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Dealer moved it to next week" />
        </div>
        <DialogFooter>
          <Button type="button" variant="destructive" disabled={cancel.loading || reason.trim() === ''} onClick={submit}>
            {cancel.loading ? <Spinner className="mr-2 h-4 w-4" /> : <XCircle className="mr-2 h-4 w-4" />}
            Cancel booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function BookingsTable({
  rows,
  failed,
  canRecord,
  onSaved,
}: {
  rows: BookingRow[]
  failed: boolean
  canRecord: boolean
  onSaved: () => void
}) {
  if (rows.length === 0) {
    return (
      <p className="p-8 text-center text-sm text-muted-foreground">
        {failed
          ? 'Nothing could be read, so this is not a statement that no car is booked. The reason is at the top of the page.'
          : 'No booking matches. Capture one when you have booked a car in with the workshop.'}
      </p>
    )
  }
  return (
    <table className="w-full text-sm">
      <thead className="sticky top-0 z-10 bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground backdrop-blur">
        <tr>
          <th className="px-4 py-2 text-left font-medium">Vehicle</th>
          <th className="px-4 py-2 text-left font-medium">Booked for</th>
          <th className="px-4 py-2 text-left font-medium">Workshop</th>
          <th className="px-4 py-2 text-left font-medium">Status</th>
          <th className="px-4 py-2 text-left font-medium">Alert</th>
          <th className="px-4 py-2 text-left font-medium">Captured</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((b) => (
          <tr key={b.booking_id} className="border-b border-border/60 align-top hover:bg-muted/40">
            <td className="px-4 py-2">
              <div className="font-semibold tabular-nums text-foreground">{b.plate ?? '—'}</div>
              <div className="text-xs text-muted-foreground">{b.ref}</div>
              {b.due_state && b.is_open && (
                <div className="mt-1">
                  <Chip tone={dueTone(b.due_state)}>{DUE_STATE_LABEL[b.due_state]}</Chip>
                </div>
              )}
            </td>
            <td className="px-4 py-2 tabular-nums">
              {dateTime(b.scheduled_at)}
              {b.is_open && b.date_passed && (
                <div className="mt-1 flex items-start gap-1 text-xs text-red-700 dark:text-red-400">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>
                    {b.days_past} day{b.days_past === 1 ? '' : 's'} ago and no stamp uploaded. The alert is back
                    on. Upload the stamp if it went in, or cancel and book again.
                  </span>
                </div>
              )}
            </td>
            <td className="px-4 py-2 text-xs">
              <div className="text-sm text-foreground">{b.provider_name}</div>
              <div className="text-muted-foreground">{b.address ?? 'Address not recorded'}</div>
              {b.maps_url && (
                <a href={b.maps_url} target="_blank" rel="noopener noreferrer" className={`${link} inline-flex items-center gap-1`}>
                  Map <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {b.provider_phone && <div className="text-muted-foreground">{b.provider_phone}</div>}
              {b.notes && <div className="mt-1 text-muted-foreground">{b.notes}</div>}
            </td>
            <td className="px-4 py-2 text-xs">
              <Chip tone={statusTone(b)}>{STATUS_LABEL[b.status] ?? b.status}</Chip>
              {b.status === 'completed' && (
                <div className="mt-1 text-muted-foreground">
                  {shortDate(b.serviced_on)} at {km(b.serviced_km)}
                  {b.stamp_photos > 0 ? ', stamp on file' : ', no stamp'}
                </div>
              )}
              {b.status === 'cancelled' && (
                <div className="mt-1 text-muted-foreground">
                  {b.cancel_reason}, {b.cancelled_by ?? ''}
                </div>
              )}
              {canRecord && b.is_open && (
                <div className="mt-1">
                  <UploadStampDialog
                    target={{
                      vehicle_id: b.vehicle_id,
                      plate: b.plate,
                      provider_name: b.provider_name,
                      context: `Filing it closes ${b.ref} and the alerts start again for the next service.`,
                    }}
                    onSaved={onSaved}
                    label="Serviced: upload stamp"
                  />
                  <CancelBookingDialog booking={b} onSaved={onSaved} />
                </div>
              )}
            </td>
            <td className="px-4 py-2 text-xs">
              {b.alerts_paused ? (
                <span className="inline-flex items-center gap-1 text-foreground">
                  <BellOff className="h-3.5 w-3.5" /> Off until the booked day
                </span>
              ) : b.is_open && b.date_passed ? (
                <span className="text-red-700 dark:text-red-400">On again: day passed, no stamp</span>
              ) : b.is_open ? (
                <span className="text-amber-700 dark:text-amber-400">On: proposed, not confirmed</span>
              ) : (
                <span className="text-muted-foreground">On, against the next service</span>
              )}
            </td>
            <td className="px-4 py-2 text-xs text-muted-foreground">
              {b.booked_by ?? 'not recorded'}
              <div>{shortDate(b.booked_at)}</div>
              {b.driver_informed_at ? (
                <div className="mt-1 text-foreground" title={b.driver_informed_note ?? undefined}>
                  Emailed {b.driver_name || 'the driver'}
                </div>
              ) : b.is_open ? (
                <div className="mt-1 text-amber-700 dark:text-amber-400">No driver emailed</div>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function CountAsServiceDialog({ c, onSaved }: { c: RepairCandidate; onSaved: () => void }) {
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState('')
  const [odo, setOdo] = useState('')
  const count = useCountRepairAsService<{ next_due_km?: number; booking_closed?: string | null }>()
  const parsed = kmEntry(odo)
  const needDate = !c.date_exact
  const needKm = c.odometer_km === null

  useEffect(() => {
    if (open) {
      setDate('')
      setOdo('')
    }
  }, [open])

  async function submit() {
    try {
      const r = await count.trigger({
        maintenance_line_id: c.maintenance_line_id,
        serviced_on: needDate ? date : null,
        odometer_km: needKm ? parsed : null,
      }).result
      toast.success(`Counted as a service on ${c.plate ?? 'the vehicle'}`, {
        description: [
          r?.next_due_km ? `Next service due at ${km(r.next_due_km)}.` : null,
          r?.booking_closed ? `Booking ${r.booking_closed} is closed.` : null,
        ]
          .filter(Boolean)
          .join(' '),
      })
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not count that line', { description: writeErrorMessage(err) })
    }
  }

  const ready = (!needDate || date !== '') && (!needKm || parsed !== null)
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" size="sm" variant="outline">
          <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Count as service
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Count as a service, {c.plate ?? 'vehicle'}</DialogTitle>
          <DialogDescription>
            &ldquo;{c.description ?? c.category}&rdquo;
            {c.date_exact ? ` on ${shortDate(c.found_on)}` : ` in ${new Date(c.found_on).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}`}
            {c.odometer_km !== null ? ` at ${km(c.odometer_km)}` : ''}. The schedule will measure the
            next service from this, with your name on it. Upload the stamp later if you have it.
          </DialogDescription>
        </DialogHeader>
        {(needDate || needKm) && (
          <div className="grid grid-cols-2 gap-3">
            {needDate && (
              <div className="space-y-1.5">
                <Label htmlFor={`rc-date-${c.maintenance_line_id}`}>Service date</Label>
                <Input id={`rc-date-${c.maintenance_line_id}`} type="date" max={today()} value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
            )}
            {needKm && (
              <div className="space-y-1.5">
                <Label htmlFor={`rc-km-${c.maintenance_line_id}`}>Odometer</Label>
                <Input id={`rc-km-${c.maintenance_line_id}`} inputMode="numeric" value={odo} onChange={(e) => setOdo(e.target.value)} placeholder="From the invoice" />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button type="button" disabled={count.loading || !ready} onClick={submit}>
            {count.loading ? <Spinner className="mr-2 h-4 w-4" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
            Count as service done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DismissDialog({ c, onSaved }: { c: RepairCandidate; onSaved: () => void }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const dismiss = useDismissServiceCandidate()

  useEffect(() => {
    if (open) setReason('')
  }, [open])

  async function submit() {
    try {
      await dismiss.trigger({ maintenance_line_id: c.maintenance_line_id, reason: reason.trim() }).result
      toast.success('Set aside', { description: 'This line will not be offered as a service again.' })
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not set that line aside', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" className={`${link} ml-3`}>
          Not a service
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Not a service, {c.plate ?? 'vehicle'}</DialogTitle>
          <DialogDescription>
            The repair line is not changed. It stops being offered here, with your reason kept.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor={`rc-why-${c.maintenance_line_id}`}>Why</Label>
          <Textarea id={`rc-why-${c.maintenance_line_id}`} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. The same invoice captured twice" />
        </div>
        <DialogFooter>
          <Button type="button" disabled={dismiss.loading || reason.trim() === ''} onClick={submit}>
            {dismiss.loading && <Spinner className="mr-2 h-4 w-4" />}
            Set aside
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function RepairCandidatesPanel({
  rows,
  canRecord,
  onSaved,
}: {
  rows: RepairCandidate[]
  canRecord: boolean
  onSaved: () => void
}) {
  const [open, setOpen] = useState(true)
  if (rows.length === 0) return null
  return (
    <div className="border-b border-border bg-amber-50/60 px-4 py-3 dark:bg-amber-950/20">
      <button type="button" className="flex w-full items-center gap-2 text-left" onClick={() => setOpen(!open)} aria-expanded={open}>
        <Search className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
        <span className="text-sm font-medium text-foreground">
          {rows.length} possible service{rows.length === 1 ? '' : 's'} found in Servicing and Repairs
        </span>
        <span className="ml-auto text-xs text-muted-foreground">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <>
          <p className="mt-1 text-xs text-muted-foreground">
            Repair lines that look like a service and are dated after the last service on file for the
            car. If one is the service, count it and the schedule measures from it. Nothing here is
            counted until a person says so.
          </p>
          <ul className="mt-2 divide-y divide-border/60 rounded-md border border-border bg-card">
            {rows.map((c) => (
              <li key={c.maintenance_line_id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-sm">
                <span className="w-24 font-semibold tabular-nums">{c.plate ?? '—'}</span>
                <span className="w-28 tabular-nums text-xs">
                  {c.date_exact ? shortDate(c.found_on) : `${new Date(c.found_on).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })}, day not known`}
                </span>
                <span className="w-24 tabular-nums text-xs">{c.odometer_km !== null ? km(c.odometer_km) : 'No odometer'}</span>
                <span className="min-w-[12rem] flex-1 text-xs text-muted-foreground">
                  {c.description ?? c.category}
                  <span className="ml-1">
                    (last on file:{' '}
                    {c.last_serviced_on ? `${shortDate(c.last_serviced_on)}, ${km(c.baseline_km)}${c.baseline_provisional ? ', inferred' : ''}` : 'none'})
                  </span>
                </span>
                {canRecord && (
                  <span className="flex items-center">
                    <CountAsServiceDialog c={c} onSaved={onSaved} />
                    <DismissDialog c={c} onSaved={onSaved} />
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
